import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal } from "@/config/antd";
import Button from "@/components/common/Button";
import Input from "@/components/common/inputs/Input";
import { useDispatch, useSelector } from "react-redux";
import {
  createAccount,
  selectAccountLoading,
  selectAccountSuccess,
  selectAccountError,
  fetchAccountByAny,
} from "@/store/redux/slices/finance_management/account_slice";
import Success from "@/components/common/alerts/Success";
import Error from "@/components/common/alerts/Error";
import { useLoader } from "@/hooks/LoaderProvider";
import { getOrganizationId } from "@/utils/token_functions";

const AddAccountDialog = ({ isVisible, onClose }) => {
  const dispatch = useDispatch();
  useSelector(selectAccountLoading);
  useSelector(selectAccountSuccess);
  const accountError = useSelector(selectAccountError);
  const [showSuccess, setShowSuccess] = useState(false);
  const { showLoader, hideLoader } = useLoader();

  const [formData, setFormData] = useState({
    accountType: "",
    accountNumber: "",
    amount: "",
    spendingAmount: "",
    duration: "",
    debitAccount: "",
    paymentMode: "",
  });
  useEffect(() => {
    const fetchAccounts = async () => {
      const organizationID = getOrganizationId();
      if (organizationID) {
        await dispatch(
          fetchAccountByAny({ key: "organizationID", value: organizationID })
        ).unwrap();
      }
    };
    fetchAccounts();
  }, [dispatch]);

  const accountTypeOptions = [
    { value: "access", label: "Access Bank" },
    { value: "gtb", label: "GT Bank" },
    { value: "zenith", label: "Zenith Bank" },
    { value: "firstbank", label: "First Bank" },
    { value: "uba", label: "UBA" },
    { value: "union", label: "Union Bank" },
    { value: "fidelity", label: "Fidelity Bank" },
    { value: "stanbic", label: "Stanbic IBTC" },
    { value: "sterling", label: "Sterling Bank" },
    { value: "wema", label: "Wema Bank" },
    { value: "fcmb", label: "FCMB" },
    { value: "ecobank", label: "Ecobank" },
    { value: "opay", label: "Opay" },
  ];

  const durationOptions = [
    { value: "1 month", label: "1 Month" },
    { value: "3 months", label: "3 Months" },
    { value: "6 months", label: "6 Months" },
    { value: "12 months", label: "12 Months" },
    { value: "24 months", label: "24 Months" },
  ];

  const handleInputChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleCreate = async () => {
    onClose();
    showLoader();
    try {
      await dispatch(createAccount(formData)).unwrap();
      setShowSuccess(true);
    } catch (error) {
      console.error("Failed to create account:", error);
    } finally {
      hideLoader();
    }
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    onClose(); // Close the main dialog
  };

  return (
    <>
      <Modal
        open={isVisible}
        onCancel={onClose}
        footer={null}
        centered
        width={600}
        closeIcon={null}
        styles={{
          body: { padding: 0 },
          content: {
            padding: 0,
            borderRadius: "16px",
            overflow: "hidden",
          },
          mask: { backgroundColor: "rgba(0, 0, 0, 0.75)" },
        }}>
        <div className="flex flex-col bg-white">
          {/* Header */}
          <div className="px-6 pt-5">
            <h3 className="heading-regular text-center font-bold text-primary mb-8">
              Add New Account
            </h3>
          </div>

          {/* Content Area */}
          <div className="px-6 pb-6">
            {/* Account Details Section */}
            <div className="mb-6">
              <h4 className="text-bold text-black mb-1">Account Details</h4>
              <p className="text-lighterGrey text-small mb-4">
                Give a name to your account and description
              </p>

              <div className="space-y-4">
                <Input
                  type="select"
                  label="BANK"
                  value={formData.accountType}
                  onChange={handleInputChange("accountType")}
                  options={accountTypeOptions}
                  placeholder="Select account type"
                />

                <Input
                  type="text"
                  label="ACCOUNT NUMBER"
                  value={formData.accountNumber}
                  onChange={handleInputChange("accountNumber")}
                  placeholder="Enter account number"
                />
              </div>
            </div>

            {/* Amount Section */}
            <div className="mb-6">
              <h4 className="text-bold text-black mb-1">
                Amount to be deposited
              </h4>
              <p className="text-lighterGrey text-small mb-4">
                Detail the amount to be added to the account limit
              </p>

              <Input
                type="text"
                label="AMOUNT (₦)"
                value={formData.amount}
                onChange={handleInputChange("amount")}
                placeholder="Enter amount"
              />
            </div>

            {/* Spending Limit Section */}
            <div className="mb-6">
              <h4 className="text-bold text-black mb-1">Spending Limit</h4>
              <p className="text-lighterGrey text-small mb-4">
                Detail the amount to be spent
              </p>

              <div className="space-y-4">
                <Input
                  type="text"
                  label="AMOUNT (₦)"
                  value={formData.spendingAmount}
                  onChange={handleInputChange("spendingAmount")}
                  placeholder="Enter spending limit"
                />

                <Input
                  type="select"
                  label="DURATION"
                  value={formData.duration}
                  onChange={handleInputChange("duration")}
                  options={durationOptions}
                  placeholder="Select duration"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="w-full bg-secondary p-4 flex gap-3">
            <Button
              text="CANCEL"
              color="secondary"
              className="flex-1 border border-primary"
              customBgColor="white"
              customTextColor="primary"
              onClick={onClose}
            />
            <Button
              text="CREATE"
              color="primary"
              customBgColor="primary"
              customTextColor="white"
              className="flex-1"
              onClick={handleCreate}
            />
          </div>
        </div>
      </Modal>

      {/* Success Dialog */}
      <Success
        isVisible={showSuccess}
        onClose={handleSuccessClose}
        header="Account Created Successfully"
        paragraph="Your new account has been created and is ready to use."
        buttonText="Continue"
        onButtonClick={handleSuccessClose}
      />

      {/* Error Dialog */}
      <Error
        isVisible={!!accountError}
        onClose={() => dispatch({ type: "account/clearError" })}
        header="Failed to Create Account"
        paragraph={
          accountError?.message ||
          "An error occurred while creating the account. Please try again."
        }
        buttonText="Try Again"
      />
    </>
  );
};

AddAccountDialog.propTypes = {
  isVisible: PropTypes.bool ,
  onClose: PropTypes.func,
};

export default AddAccountDialog;
